import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { authApi } from "@/api/authApi";
import { ROUTES } from "@/constants/routes";
import { authService } from "@/services/authService";
import { useUserStore } from "@/store/userStore";
import type { User, UserRegisterRequest } from "@/types/auth";

export const AUTH_QUERY_KEYS = {
  USER: ["auth", "user"] as const,
};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const setUser = useUserStore((s) => s.setUser);
  const clearUser = useUserStore((s) => s.clearUser);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const setAuthenticated = useUserStore((s) => s.setAuthenticated);

  const userQuery = useQuery<User>({
    queryKey: AUTH_QUERY_KEYS.USER,
    queryFn: () => authApi.me(),
    enabled: isAuthenticated,
    retry: false,
  });

  useEffect(() => {
    if (userQuery.data) {
      setUser(userQuery.data);
    }
  }, [userQuery.data, setUser]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      await authService.login(email, password);
    },
    onSuccess: () => {
      setAuthenticated(true);
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.USER });
      navigate(ROUTES.dashboard);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: async (data: UserRegisterRequest) => {
      await authService.register(data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.USER });
      navigate(ROUTES.login);
    },
  });

  // Logout
  const logout = async () => {
    setAuthenticated(false);
    clearUser();
    queryClient.clear();

    await authService.logout();

    navigate(ROUTES.login, { replace: true });
  };

  return {
    // State
    user: userQuery.data,
    isLoading: userQuery.isLoading,
    isAuthenticated,

    // Actions
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    logout,

    // Mutation states
    isLoginLoading: loginMutation.isPending,
    isRegisterLoading: registerMutation.isPending,
    loginError: loginMutation.error,
    registerError: registerMutation.error,
  };
};
